import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useGetHorariosLojaQuery, type HorarioFuncionamentoLoja } from '../api';
import { useLojaFechadaDelivery } from './use-loja-fechada-delivery'

const DIAS = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

function hhmm(hora: string): string {
  return hora.slice(0, 5);
}

/**
 * Badge "Aberto agora"/"Fechado no momento" da loja (status calculado no
 * backend com a mesma regra que bloqueia pedidos fora de horário). Clicar
 * abre o diálogo com a grade de horários de atendimento.
 */
export function StatusLoja(): JSX.Element | null {
  const { data } = useGetHorariosLojaQuery(undefined, { pollingInterval: 5 * 60_000 });
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const estado = useLojaFechadaDelivery()

  if (!data) return null;

  const hoje = new Date().getDay();
  const porDia = new Map<number, HorarioFuncionamentoLoja[]>();
  for (const h of data.horarios) {
    const lista = porDia.get(h.diaSemana) ?? [];
    lista.push(h);
    porDia.set(h.diaSemana, lista);
  }

  return (
    <>
      <Chip
        size="small"
        icon={<CircleIcon sx={{ fontSize: 10 }} />}
        label={data.aberta ? 'Aberto agora' : 'Fechado no momento'}
        color={data.aberta ? 'success' : 'error'}
        variant="outlined"
        onClick={() => setDialogoAberto(true)}
        title="Ver horários de atendimento"
      />

      <Dialog open={dialogoAberto} onClose={() => setDialogoAberto(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon fontSize="small" />
          Horários de atendimento
        </DialogTitle>
        <DialogContent>
          {!data.aberta && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {estado?.ofertavel
                ? 'A loja está fechada agora. Você pode pedir mesmo assim: no final do pedido, escolha um dos horários abaixo.'
                : 'A loja está fechada no momento — pedidos só são aceitos dentro dos horários abaixo.'}
            </Alert>
          )}
          {data.horarios.length === 0 ? (
            <Alert severity="info">A loja ainda não informou seus horários de atendimento.</Alert>
          ) : (
            <Stack spacing={1}>
              {DIAS.map((nome, dia) => {
                const faixas = porDia.get(dia);
                const ehHoje = dia === hoje;
                return (
                  <Stack
                    key={dia}
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: ehHoje ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Typography fontWeight={ehHoje ? 700 : 400}>
                      {nome}
                      {ehHoje ? ' · hoje' : ''}
                    </Typography>
                    <Typography
                      fontWeight={ehHoje ? 700 : 400}
                      color={faixas ? 'text.primary' : 'text.secondary'}
                      align="right"
                    >
                      {faixas ? faixas.map((f) => `${hhmm(f.horaInicio)}–${hhmm(f.horaFim)}`).join(', ') : 'Fechado'}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
